import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  ListGroup,
  Modal,
  Button,
  Carousel,
  Row,
  Col,
} from "react-bootstrap";
import {
  FiShoppingBag,
  FiCalendar,
  FiArrowUpRight,
  FiUsers,
  FiInfo,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiBarChart2,
  FiClock,
  FiLayers,
  FiMaximize2,
} from "react-icons/fi";

const InfoOrdiniDettagliato = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Stati separati per gestire l'indice del carosello nel widget e nel modale
  const [indexWidget, setIndexWidget] = useState(0);
  const [indexModal, setIndexModal] = useState(0);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        setRawData(json?.ORDINI_URGENTI?.flat() || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore statistiche:", err);
        setLoading(false);
      });
  }, []);

  const stats = useMemo(() => {
    const adesso = new Date();
    const offset = adesso.getTimezoneOffset() * 60000;
    const oggiLocaleStr = new Date(adesso - offset).toISOString().split("T")[0];

    // Periodo mobile di 7 giorni: oggi + i 6 giorni precedenti
    const ultime7Date = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(adesso - offset);
      d.setDate(d.getDate() - i);
      ultime7Date.push(d.toISOString().split("T")[0]);
    }

    const mapOggi = {};
    const mapSettimana = {};
    let totaleOggi = 0;
    let totaleSettimana = 0;

    rawData.forEach((item) => {
      const importo = parseFloat(item["Importo"]) || 0;
      const fornitore = item["Ragione sociale"] || "Sconosciuto";
      const dataDocStr = item["Data Ordine"]?.split(" ")[0];

      if (!dataDocStr) return;

      // ACQUISTATO OGGI
      if (dataDocStr === oggiLocaleStr) {
        totaleOggi += importo;
        mapOggi[fornitore] = (mapOggi[fornitore] || 0) + importo;
      }

      // ULTIMI 7 GIORNI, COMPRESO OGGI
      if (ultime7Date.includes(dataDocStr)) {
        totaleSettimana += importo;
        mapSettimana[fornitore] = (mapSettimana[fornitore] || 0) + importo;
      }
    });

    // Elenco fornitori ordinato dal maggiore al minore importo
    const formatLista = (obj) =>
      Object.entries(obj)
        .map(([nome, valore]) => ({ nome, valore }))
        .sort((a, b) => b.valore - a.valore);

    return {
      oggi: totaleOggi,
      listaOggi: formatLista(mapOggi),
      settimana: totaleSettimana,
      listaSettimana: formatLista(mapSettimana),
    };
  }, [rawData]);

  const formatEuro = (value) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const handlePrev = (isModal) => {
    if (isModal) {
      setIndexModal((prev) => (prev === 0 ? 1 : 0));
    } else {
      setIndexWidget((prev) => (prev === 0 ? 1 : 0));
    }
  };

  const handleNext = (isModal) => {
    if (isModal) {
      setIndexModal((prev) => (prev === 1 ? 0 : 1));
    } else {
      setIndexWidget((prev) => (prev === 1 ? 0 : 1));
    }
  };

  const RenderLista = ({ lista, color, isFullscreen = false }) => (
    <div
      className="custom-scroll"
      style={{
        height: isFullscreen ? "450px" : "150px",
        overflowY: "auto",
      }}
    >
      <ListGroup variant="flush" className="bg-transparent">
        {lista.length > 0 ? (
          lista.map((f, i) => (
            <ListGroup.Item
              key={i}
              className="d-flex justify-content-between align-items-center px-0 py-2 border-bottom border-secondary border-opacity-10 bg-transparent"
            >
              <div className="d-flex align-items-center overflow-hidden">
                <div
                  className={`me-2 rounded-circle bg-${color}`}
                  style={{ width: "5px", height: "5px", flexShrink: 0 }}
                />
                <small
                  className="text-dark text-truncate"
                  style={{
                    fontSize: isFullscreen ? "1rem" : "0.75rem",
                    fontWeight: "500",
                  }}
                >
                  {f.nome}
                </small>
              </div>

              <span
                className="fw-bold text-dark ms-2"
                style={{ fontSize: isFullscreen ? "1.1rem" : "0.8rem" }}
              >
                {formatEuro(f.valore)}
              </span>
            </ListGroup.Item>
          ))
        ) : (
          <div className="text-muted p-2 small fst-italic">
            Nessun ordine registrato
          </div>
        )}
      </ListGroup>
    </div>
  );

  const CardOggi = ({ isFullscreen }) => (
    <div
      className={`p-4 ${isFullscreen ? "p-md-5 mx-auto" : ""}`}
      style={isFullscreen ? { maxWidth: "800px" } : {}}
    >
      <div className="d-flex align-items-center mb-4">
        <div
          className="icon-box text-danger p-2 rounded-3 me-3 d-flex align-items-center justify-content-center"
          style={{
            width: isFullscreen ? "60px" : "42px",
            height: isFullscreen ? "60px" : "42px",
          }}
        >
          <FiShoppingBag size={isFullscreen ? 28 : 20} />
        </div>

        <div>
          <small
            className="text-uppercase text-muted fw-bold"
            style={{
              fontSize: isFullscreen ? "0.8rem" : "0.6rem",
              letterSpacing: "0.5px",
            }}
          >
            Acquistato Oggi
          </small>

          <h2
            className="mb-0 fw-bold text-dark"
            style={{
              letterSpacing: "-1px",
              fontSize: isFullscreen ? "2.8rem" : "1.7rem",
            }}
          >
            {loading ? "..." : formatEuro(stats.oggi)}
          </h2>
        </div>
      </div>

      <div
        className="mb-2 d-flex align-items-center text-muted"
        style={{
          fontSize: isFullscreen ? "0.8rem" : "0.65rem",
          fontWeight: "700",
          letterSpacing: "0.3px",
        }}
      >
        <FiUsers className="me-1 text-danger" /> TOP FORNITORI DEL GIORNO
      </div>

      <RenderLista
        lista={stats.listaOggi}
        color="danger"
        isFullscreen={isFullscreen}
      />
    </div>
  );

  const CardSettimana = ({ isFullscreen }) => (
    <div
      className={`p-4 ${isFullscreen ? "p-md-5 mx-auto" : ""}`}
      style={isFullscreen ? { maxWidth: "800px" } : {}}
    >
      <div className="d-flex align-items-center mb-4">
        <div
          className="icon-box text-warning p-2 rounded-3 me-3 d-flex align-items-center justify-content-center"
          style={{
            width: isFullscreen ? "60px" : "42px",
            height: isFullscreen ? "60px" : "42px",
          }}
        >
          <FiCalendar size={isFullscreen ? 28 : 20} />
        </div>

        <div>
          <small
            className="text-uppercase text-muted fw-bold"
            style={{
              fontSize: isFullscreen ? "0.8rem" : "0.6rem",
              letterSpacing: "0.5px",
            }}
          >
            Ultimi 7 Giorni
          </small>

          <h2
            className="mb-0 fw-bold text-dark"
            style={{
              letterSpacing: "-1px",
              fontSize: isFullscreen ? "2.8rem" : "1.7rem",
            }}
          >
            {loading ? "..." : formatEuro(stats.settimana)}
          </h2>
        </div>
      </div>

      <div
        className="mb-2 d-flex align-items-center text-muted"
        style={{
          fontSize: isFullscreen ? "0.8rem" : "0.65rem",
          fontWeight: "700",
          letterSpacing: "0.3px",
        }}
      >
        <FiArrowUpRight className="me-1 text-warning" /> TREND SETTIMANALE
      </div>

      <RenderLista
        lista={stats.listaSettimana}
        color="warning"
        isFullscreen={isFullscreen}
      />
    </div>
  );

  const RenderControlliFrecce = ({ isModal }) => (
    <div
      className="d-flex gap-1 ms-auto align-items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        variant="light"
        size="sm"
        className="p-1 d-flex align-items-center justify-content-center rounded-circle border border-secondary border-opacity-10"
        style={{ width: "28px", height: "28px" }}
        onClick={() => handlePrev(isModal)}
      >
        <FiChevronLeft size={16} color="#495057" />
      </Button>

      <Button
        variant="light"
        size="sm"
        className="p-1 d-flex align-items-center justify-content-center rounded-circle border border-secondary border-opacity-10"
        style={{ width: "28px", height: "28px" }}
        onClick={() => handleNext(isModal)}
      >
        <FiChevronRight size={16} color="#495057" />
      </Button>
    </div>
  );

  return (
    <>
      <Card
        className="border-0"
        style={{
          borderRadius: "10px",
          overflow: "hidden",
          height: "420px",
          backgroundColor: "#ffffff",
        }}
      >
        <style>{`
          .custom-scroll::-webkit-scrollbar { width: 4px; }
          .custom-scroll::-webkit-scrollbar-track { background: transparent; }
          .custom-scroll::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }
          .icon-box { background: rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.05); }

          .custom-carousel .carousel-indicators [data-bs-target] {
            background-color: #000;
            width: 6px;
            height: 6px;
            border-radius: 50%;
          }

          .custom-carousel .carousel-indicators {
            bottom: 5px;
          }

          .modal-carousel-container .custom-carousel .carousel-indicators {
            bottom: -20px;
          }

          .acquisti-info-btn {
            width: 27px;
            height: 27px;
            border: 1px solid #dfe3e8;
            border-radius: 50%;
            background: #fff;
            color: #0d6efd;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            transition: all 0.15s ease;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
            cursor: pointer;
          }

          .acquisti-info-btn:hover {
            background: #e7f1ff;
            border-color: #b6d4fe;
            transform: translateY(-1px);
          }

          .acquisti-info-backdrop {
            z-index: 20990 !important;
          }

          .acquisti-info-card {
            border: 1px solid #e9ecef;
            border-radius: 14px;
            background: #fff;
            padding: 16px;
            height: 100%;
          }

          .acquisti-info-step {
            width: 28px;
            height: 28px;
            min-width: 28px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.72rem;
            font-weight: 800;
            background: #e7f1ff;
            color: #0d6efd;
          }

          .acquisti-info-list {
            margin: 0;
            padding-left: 18px;
            color: #495057;
            font-size: 0.82rem;
          }

          .acquisti-info-list li + li {
            margin-top: 7px;
          }
        `}</style>

        {/* HEADER WIDGET */}
        <div
          className="p-3 border-bottom d-flex align-items-center bg-white"
          style={{ zIndex: 110 }}
        >
          <div className="d-flex align-items-center">
            <span
              onClick={() => setShowFullscreenModal(true)}
              style={{
                fontWeight: "800",
                fontSize: "0.75rem",
                color: "#1a1a1a",
                letterSpacing: "1px",
                marginRight: "8px",
                cursor: "pointer",
              }}
            >
              INFO ACQUISTI
            </span>

            <button
              type="button"
              className="acquisti-info-btn"
              onClick={() => setShowInfoModal(true)}
              aria-label="Informazioni sul widget Info Acquisti"
            >
              <FiInfo size={14} />
            </button>
          </div>

          {/* Frecce integrate nell'header */}
          <RenderControlliFrecce isModal={false} />
        </div>

        <Card.Body className="p-0 d-flex flex-column justify-content-center">
          {/* interval={null} blocca lo scorrimento automatico */}
          <Carousel
            activeIndex={indexWidget}
            onSelect={(selIndex) => setIndexWidget(selIndex)}
            indicators={true}
            controls={false}
            interval={null}
            className="w-100 h-100 custom-carousel"
          >
            <Carousel.Item>
              <CardOggi isFullscreen={false} />
            </Carousel.Item>

            <Carousel.Item>
              <CardSettimana isFullscreen={false} />
            </Carousel.Item>
          </Carousel>
        </Card.Body>
      </Card>

      {/* =========================================================
          MODALE INFORMAZIONI
      ========================================================= */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        scrollable
        className="p-5"
        style={{ zIndex: 21000 }}
        backdropClassName="acquisti-info-backdrop"
      >
        <Modal.Header closeButton className="border-bottom bg-white px-4 py-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
              style={{ width: "40px", height: "40px" }}
            >
              <FiShoppingBag size={20} />
            </div>

            <div>
              <Modal.Title className="fw-bold" style={{ fontSize: "1.05rem" }}>
                Come leggere Info Acquisti
              </Modal.Title>

              <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
                Guida ai totali acquistati, ai periodi considerati e alla
                classifica dei fornitori.
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          {/* INTRODUZIONE */}
          <div className="bg-primary-subtle border border-primary-subtle rounded-4 p-3 mb-3">
            <div className="fw-bold text-primary mb-1">A cosa serve</div>
            <div
              className="text-dark"
              style={{ fontSize: "0.84rem", lineHeight: 1.55 }}
            >
              Il widget riepiloga il valore degli ordini fornitore presenti nei
              dati e permette di confrontare rapidamente quanto è stato ordinato
              <strong> oggi</strong> e negli <strong>ultimi 7 giorni</strong>.
              Per ciascun periodo mostra inoltre i fornitori ordinati per valore
              economico complessivo.
            </div>
          </div>

          <Row className="g-3 mb-3">
            {/* SEZIONE 1 */}
            <Col md={6}>
              <div className="acquisti-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="acquisti-info-step">1</span>
                  <div>
                    <div className="fw-bold text-dark">Acquistato Oggi</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Totale degli ordini con data odierna
                    </div>
                  </div>
                </div>

                <ul className="acquisti-info-list">
                  <li>
                    Il sistema legge il campo <strong>Data Ordine</strong> di ogni
                    riga.
                  </li>
                  <li>
                    Entrano nel totale solamente le righe la cui Data Ordine
                    corrisponde alla <strong>data di oggi</strong>.
                  </li>
                  <li>
                    Per queste righe viene sommato il campo <strong>Importo</strong>.
                  </li>
                  <li>
                    Il risultato rappresenta quindi il valore economico degli
                    ordini fornitore registrati nella giornata corrente.
                  </li>
                </ul>
              </div>
            </Col>

            {/* SEZIONE 2 */}
            <Col md={6}>
              <div className="acquisti-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="acquisti-info-step">2</span>
                  <div>
                    <div className="fw-bold text-dark">Ultimi 7 Giorni</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Periodo mobile, compreso il giorno corrente
                    </div>
                  </div>
                </div>

                <ul className="acquisti-info-list">
                  <li>
                    Il periodo comprende <strong>oggi + i 6 giorni precedenti</strong>.
                  </li>
                  <li>
                    Non si tratta della settimana di calendario da lunedì a
                    domenica, ma di una <strong>finestra mobile di 7 giorni</strong>.
                  </li>
                  <li>
                    Per tutte le righe comprese nel periodo viene sommato il campo
                    <strong> Importo</strong>.
                  </li>
                  <li>
                    Il valore di oggi è quindi già compreso anche nel totale degli
                    ultimi 7 giorni.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* SEZIONE 3 */}
          <div className="acquisti-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="acquisti-info-step">3</span>
              <div>
                <div className="fw-bold text-dark">Classifica fornitori</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Come vengono calcolati gli importi per fornitore
                </div>
              </div>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="border rounded-4 p-3 h-100 bg-light">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FiUsers className="text-primary" />
                    <strong>Raggruppamento</strong>
                  </div>
                  <div
                    className="text-muted"
                    style={{ fontSize: "0.8rem", lineHeight: 1.5 }}
                  >
                    Le righe vengono raggruppate per <strong>Ragione sociale</strong>.
                    Se lo stesso fornitore compare su più ordini, i relativi
                    importi vengono sommati.
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="border rounded-4 p-3 h-100 bg-light">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FiBarChart2 className="text-primary" />
                    <strong>Ordinamento</strong>
                  </div>
                  <div
                    className="text-muted"
                    style={{ fontSize: "0.8rem", lineHeight: 1.5 }}
                  >
                    Dopo il raggruppamento, i fornitori vengono ordinati dal
                    <strong> maggiore al minore valore acquistato</strong>. Il primo
                    della lista è quindi quello con il valore più alto nel periodo
                    visualizzato.
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <Row className="g-3 mb-3">
            {/* SEZIONE 4 */}
            <Col md={6}>
              <div className="acquisti-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="acquisti-info-step">4</span>
                  <div>
                    <div className="fw-bold text-dark">Origine degli importi</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Valore utilizzato nel calcolo
                    </div>
                  </div>
                </div>

                <div
                  className="text-dark mb-3"
                  style={{ fontSize: "0.82rem", lineHeight: 1.55 }}
                >
                  Il calcolo usa direttamente il campo <strong>Importo</strong>
                  presente in ogni riga del file dati.
                </div>

                <div className="p-3 rounded-3 bg-light border">
                  <div className="fw-bold mb-1" style={{ fontSize: "0.8rem" }}>
                    Se l'importo non è valorizzato
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Il widget lo considera pari a <strong>€ 0,00</strong>, evitando
                    che un valore mancante blocchi il calcolo del totale.
                  </div>
                </div>
              </div>
            </Col>

            {/* SEZIONE 5 */}
            <Col md={6}>
              <div className="acquisti-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="acquisti-info-step">5</span>
                  <div>
                    <div className="fw-bold text-dark">Gestione delle date</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Confronto per giorno, senza considerare l'orario
                    </div>
                  </div>
                </div>

                <ul className="acquisti-info-list">
                  <li>
                    Dal campo <strong>Data Ordine</strong> viene utilizzata la sola
                    parte della data nel formato anno-mese-giorno.
                  </li>
                  <li>
                    L'eventuale orario presente nella Data Ordine non modifica
                    l'appartenenza al giorno.
                  </li>
                  <li>
                    Se una riga non contiene una Data Ordine valida, non viene
                    inclusa nei calcoli temporali del widget.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* SEZIONE 6 */}
          <div className="acquisti-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="acquisti-info-step">6</span>
              <div>
                <div className="fw-bold text-dark">Esempio pratico</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Come viene costruito il totale di un fornitore
                </div>
              </div>
            </div>

            <div className="p-3 rounded-4 border bg-light">
              <div className="fw-bold mb-3">Esempio — Fornitore Alfa</div>

              <Row className="g-2">
                <Col md={4}>
                  <div className="bg-white border rounded-3 p-3 h-100">
                    <div className="text-muted small">Ordine 1</div>
                    <div className="fw-bold">€ 5.000,00</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="bg-white border rounded-3 p-3 h-100">
                    <div className="text-muted small">Ordine 2</div>
                    <div className="fw-bold">€ 3.500,00</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="bg-white border rounded-3 p-3 h-100">
                    <div className="text-muted small">Totale fornitore</div>
                    <div className="fw-bold text-primary">€ 8.500,00</div>
                  </div>
                </Col>
              </Row>

              <div className="mt-3 text-muted" style={{ fontSize: "0.78rem" }}>
                Se entrambi gli ordini ricadono nel periodo visualizzato, il
                fornitore comparirà in classifica con il totale di € 8.500,00.
              </div>
            </div>
          </div>

          {/* SEZIONE 7 */}
          <div className="acquisti-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="acquisti-info-step">7</span>
              <div>
                <div className="fw-bold text-dark">Navigazione del widget</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Carosello e vista a schermo intero
                </div>
              </div>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="d-flex gap-3 align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
                    style={{ width: "36px", height: "36px" }}
                  >
                    <FiLayers size={16} />
                  </div>
                  <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                    Usa le frecce nell'header per passare dalla scheda
                    <strong> Acquistato Oggi</strong> alla scheda
                    <strong> Ultimi 7 Giorni</strong>. Lo scorrimento automatico è
                    disattivato.
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="d-flex gap-3 align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
                    style={{ width: "36px", height: "36px" }}
                  >
                    <FiMaximize2 size={16} />
                  </div>
                  <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                    Cliccando sul titolo <strong>INFO ACQUISTI</strong> si apre la
                    vista a schermo intero, utile per consultare l'elenco completo
                    dei fornitori con maggiore spazio.
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
            Il totale degli ultimi 7 giorni comprende anche gli acquisti di oggi.
          </div>

          <div className="d-flex gap-2">
            <Button variant="light" onClick={() => setShowInfoModal(false)}>
              Chiudi
            </Button>

            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreenModal(true);
              }}
            >
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* =========================================================
          MODALE TUTTO SCHERMO
      ========================================================= */}
      <Modal
        show={showFullscreenModal}
        onHide={() => setShowFullscreenModal(false)}
        fullscreen={true}
        animation={true}
      >
        <Modal.Body
          className="p-0 d-flex flex-column"
          style={{ background: "#f8f9fa" }}
        >
          {/* Header Modale */}
          <div
            className="p-3 bg-white border-bottom d-flex align-items-center shadow-sm"
            style={{ zIndex: 110 }}
          >
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontWeight: "800",
                  fontSize: "0.9rem",
                  color: "#1a1a1a",
                  letterSpacing: "1px",
                  marginRight: "8px",
                }}
              >
                INFO ACQUISTI
              </span>

              <button
                type="button"
                className="acquisti-info-btn"
                onClick={() => setShowInfoModal(true)}
                aria-label="Informazioni sul widget Info Acquisti"
              >
                <FiInfo size={16} />
              </button>
            </div>

            {/* Frecce integrate nell'header del modale */}
            <div className="ms-4 d-flex align-items-center flex-grow-1">
              <RenderControlliFrecce isModal={true} />
            </div>

            <Button
              variant="outline-dark"
              className="d-flex align-items-center gap-1 border-0 ms-3"
              onClick={() => setShowFullscreenModal(false)}
              style={{ borderRadius: "10px", height: "38px" }}
            >
              <FiX size={16} /> Chiudi
            </Button>
          </div>

          <div
            className="flex-grow-1 p-4 d-flex align-items-center justify-content-center modal-carousel-container"
            style={{ overflowY: "auto" }}
          >
            <div
              className="bg-white shadow-sm rounded-4 border w-100 p-3 pb-5"
              style={{ maxWidth: "900px" }}
            >
              {/* interval={null} blocca lo scorrimento automatico anche qui */}
              <Carousel
                activeIndex={indexModal}
                onSelect={(selIndex) => setIndexModal(selIndex)}
                indicators={true}
                controls={false}
                interval={null}
                className="w-100 h-100 custom-carousel"
              >
                <Carousel.Item>
                  <CardOggi isFullscreen={true} />
                </Carousel.Item>

                <Carousel.Item>
                  <CardSettimana isFullscreen={true} />
                </Carousel.Item>
              </Carousel>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default InfoOrdiniDettagliato;